<template>
  <!-- 产品分组管理模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              产品分组管理
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              创建和管理产品分组，方便产品分类
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200',
                'flex-1 sm:flex-initial text-center sm:text-left',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 创建分组 Tab -->
        <div v-show="activeTab === 'create'" class="space-y-6 mb-6">
          <div>
            <label class="text-sm font-medium text-gray-700 mb-3 block">创建新分组</label>
            <div class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组名称 <span class="text-red-500">*</span></label>
                  <input
                    v-model="newGroup.name"
                    type="text"
                    placeholder="输入分组名称"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                    @keyup.enter="handleCreateGroup"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
                  <input
                    v-model="newGroup.description"
                    type="text"
                    placeholder="描述该分组用途（可选）"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    :disabled="submitting"
                  />
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">分组颜色</label>
                  <div class="flex items-center gap-3">
                    <input
                      v-model="newGroup.color"
                      type="color"
                      class="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                      :disabled="submitting"
                    />
                    <span class="text-sm text-gray-600">选择分组标识颜色</span>
                  </div>
                </div>
                <div>
                  <button
                    @click="handleCreateGroup"
                    :disabled="!newGroup.name.trim() || submitting"
                    class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {{ submitting ? '创建中...' : '创建分组' }}
                  </button>
                </div>
              </div>

              <div v-if="Object.keys(errors).length > 0" class="mt-4">
                <p v-for="(error, key) in errors" :key="key" class="text-sm text-red-600">{{ error }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 分组管理 Tab -->
        <div v-show="activeTab === 'manage'" class="space-y-6 mb-6">
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                产品分组 
                <span class="text-sm text-gray-500">({{ filteredGroups.length }}{{ searchQuery ? ' / ' + groups.length : '' }})</span>
              </label>
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索分组..."
                  class="w-32 sm:w-48 h-8 pl-8 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div v-if="loading" class="flex flex-col items-center justify-center py-8">
              <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="filteredGroups.length === 0" class="flex flex-col items-center justify-center text-center text-gray-500 py-8">
              <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p class="text-lg font-medium mb-2">
                {{ searchQuery ? '未找到匹配的分组' : '暂无分组' }}
              </p>
              <p class="text-sm">
                {{ searchQuery ? '尝试使用其他关键词搜索' : '点击"创建分组"标签页创建第一个产品分组' }}
              </p>
            </div>

            <div v-else class="max-h-[30vh] overflow-y-auto pr-2 -mr-2" style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
              <div class="grid grid-cols-2 gap-2 items-start content-start">
                <div
                  v-for="group in filteredGroups"
                  :key="group.id"
                  class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors min-h-[40px]"
                >
                  <!-- 分组显示 -->
                  <div class="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1 overflow-hidden">
                    <span
                      :style="{ backgroundColor: group.color }"
                      class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 text-white max-w-[120px] sm:max-w-none overflow-hidden"
                      :title="group.description || group.name"
                    >
                      <span class="truncate">{{ group.name }}</span>
                    </span>
                    <span class="text-xs text-gray-500 flex-shrink-0">
                      {{ group.product_count || 0 }}
                    </span>
                  </div>

                  <!-- 操作按钮 -->
                  <div class="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <button
                      @click="handleEditGroup(group)"
                      class="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center"
                      title="编辑分组"
                    >
                      ✏️
                    </button>
                    <button
                      @click="handleDeleteGroup(group)"
                      :disabled="group.product_count > 0"
                      class="text-red-600 hover:text-red-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center disabled:text-gray-400 disabled:cursor-not-allowed"
                      :title="group.product_count > 0 ? '该分组下还有产品，无法删除' : '删除分组'"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            @click="handleCancel"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 编辑分组模态框 -->
  <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
    <div class="bg-white rounded-lg shadow-xl w-[90vw] max-w-[90vw] sm:max-w-lg mx-auto">
      <div class="p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">编辑产品分组</h3>
          <button
            @click="handleCloseEditModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 编辑表单 -->
        <div v-if="editingGroup" class="space-y-4">
          <!-- 分组名称 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              分组名称 <span class="text-red-500">*</span>
            </label>
            <input
              v-model="editingGroup.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="输入分组名称"
              :disabled="submitting"
            />
          </div>

          <!-- 分组描述 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
            <input
              v-model="editingGroup.description"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="描述该分组用途（可选）"
              :disabled="submitting"
            />
          </div>

          <!-- 分组颜色 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">分组颜色</label>
            <div class="flex items-center gap-3">
              <input
                v-model="editingGroup.color"
                type="color"
                class="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                :disabled="submitting"
              />
              <span class="text-sm text-gray-600">选择分组标识颜色</span>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="flex justify-end space-x-3 pt-4">
            <button
              @click="handleCloseEditModal"
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              :disabled="submitting"
            >
              取消
            </button>
            <button
              @click="handleUpdateGroup"
              :disabled="!editingGroup.name?.trim() || submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {{ submitting ? '更新中...' : '更新分组' }}
            </button>
          </div>

          <!-- 错误信息 -->
          <div v-if="Object.keys(errors).length > 0" class="mt-4">
            <p v-for="(error, key) in errors" :key="key" class="text-sm text-red-600">{{ error }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import { productGroupService } from '@/services/productService'

const toast = useToast()

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// 响应式数据
const loading = ref(false)
const submitting = ref(false)
const groups = ref([])
const searchQuery = ref('')
const activeTab = ref('create')
const editingGroup = ref(null)
const showEditModal = ref(false)

const tabs = ref([
  { id: 'create', name: '创建分组' },
  { id: 'manage', name: '分组管理' }
])
const errors = ref({})

const newGroup = ref({
  name: '',
  description: '',
  color: '#3B82F6'
})

// 计算属性
const filteredGroups = computed(() => {
  if (!searchQuery.value) {
    return groups.value
  }
  const query = searchQuery.value.toLowerCase()
  return groups.value.filter(group => 
    group.name.toLowerCase().includes(query) ||
    (group.description && group.description.toLowerCase().includes(query))
  )
})

// 监听器
watch(() => props.show, (show) => {
  if (show) {
    loadGroups()
    resetForm()
    activeTab.value = 'create'
  }
})

// 方法
const resetForm = () => {
  newGroup.value = {
    name: '',
    description: '',
    color: '#3B82F6'
  }
  searchQuery.value = ''
  activeTab.value = 'create'
  errors.value = {}
}

const loadGroups = async () => {
  loading.value = true
  try {
    const response = await productGroupService.getProductGroups({
      page_size: 100
    })
    if (response.success) {
      groups.value = response.results
    }
  } catch (error) {
    console.error('加载分组失败:', error)
    toast.error('加载分组列表失败')
  } finally {
    loading.value = false
  }
}

const handleCreateGroup = async () => {
  if (!newGroup.value.name.trim()) {
    errors.value.name = '请输入分组名称'
    return
  }

  submitting.value = true
  errors.value = {}

  try {
    const response = await productGroupService.createProductGroup({
      name: newGroup.value.name.trim(),
      description: newGroup.value.description.trim(),
      color: newGroup.value.color
    })

    if (response.success) {
      toast.success('分组创建成功')
      newGroup.value = {
        name: '',
        description: '',
        color: '#3B82F6'
      }
      await loadGroups()
      activeTab.value = 'manage'  // 跳转到管理Tab查看创建的分组
      emit('success')
    }
  } catch (error) {
    console.error('创建分组失败:', error)
    
    if (error.response?.status === 400 && error.response?.data) {
      const errorData = error.response.data
      Object.keys(errorData).forEach(key => {
        if (errorData[key] && Array.isArray(errorData[key])) {
          errors.value[key] = errorData[key][0]
        }
      })
    } else {
      toast.error('创建分组失败')
    }
  } finally {
    submitting.value = false
  }
}

const handleEditGroup = (group) => {
  editingGroup.value = { ...group }
  showEditModal.value = true
}

const handleUpdateGroup = async () => {
  if (!editingGroup.value) return
  
  submitting.value = true
  errors.value = {}
  
  try {
    const groupData = {
      name: editingGroup.value.name.trim(),
      description: editingGroup.value.description.trim(),
      color: editingGroup.value.color
    }

    const response = await productGroupService.updateProductGroup(editingGroup.value.id, groupData)

    if (response.success) {
      toast.success('分组更新成功')
      showEditModal.value = false
      editingGroup.value = null
      await loadGroups()
      emit('success')
    }
  } catch (error) {
    console.error('更新分组失败:', error)
    
    if (error.response?.status === 400 && error.response?.data) {
      const errorData = error.response.data
      Object.keys(errorData).forEach(key => {
        if (errorData[key] && Array.isArray(errorData[key])) {
          errors.value[key] = errorData[key][0]
        }
      })
    } else {
      toast.error('更新分组失败')
    }
  } finally {
    submitting.value = false
  }
}

const handleCloseEditModal = () => {
  showEditModal.value = false
  editingGroup.value = null
  errors.value = {}
}

const handleDeleteGroup = async (group) => {
  if (group.product_count > 0) {
    toast.error('该分组下还有产品，无法删除')
    return
  }

  if (confirm(`确定要删除分组"${group.name}"吗？`)) {
    try {
      await productGroupService.deleteProductGroup(group.id)
      toast.success('分组删除成功')
      await loadGroups()
      emit('success')
    } catch (error) {
      console.error('删除分组失败:', error)
      toast.error('删除分组失败')
    }
  }
}

const handleCancel = () => {
  emit('close')
}
</script>
